import type { CardManagementStatementDocument } from 'src/queries/physicalCardManagement/physicalCardManagement.models'

/**
 * A document with its file name already decided.
 *
 * Narrower than `CardManagementStatementDocument`, whose `fileName` is
 * nullable: by the time bytes reach the browser something has to have chosen a
 * name, and requiring it here is what stops an unnamed download reaching
 * `<a download>`.
 */
export type DeliverableStatementDocument = Omit<
  CardManagementStatementDocument,
  'fileName'
> & { fileName: string }

/**
 * Hands a fetched statement document to the browser (EX-40602).
 *
 * The download route publishes bytes and no url — no signed url, storage path,
 * or bucket — and it is reachable only over the authenticated session, so the
 * document cannot be given to the browser as a link. Everything below therefore
 * runs against an in-memory object url.
 *
 * ## Why PDF is opened rather than saved
 *
 * A statement is usually READ, not filed, and every browser renders a PDF
 * inline; a spreadsheet is the opposite — nothing renders XLSX, and a CSV shown
 * as plain text in a tab is a worse answer than a file on disk. So PDF opens in
 * a new tab and the rest download, which is also what the ticket specifies.
 *
 * The one thing this cannot do is respect the server's `Content-Disposition:
 * attachment` for the PDF: an object url carries no headers, so the disposition
 * is ours to choose here, per format.
 */

/**
 * How long a NEW-TAB object url outlives delivery before being revoked.
 *
 * Only the tab path waits. `window.open` returns before the new document has
 * navigated, so revoking immediately there lands the user on a blank tab; a
 * download is already committed by the time `anchor.click()` returns and revokes
 * on the spot. A statement PDF can run to megabytes, and the blob stays pinned
 * in memory for this entire window, so the wait belongs only where it buys
 * something.
 */
const NEW_TAB_OBJECT_URL_LIFETIME_MS = 60_000

export interface StatementDocumentDeliveryResult {
  /**
   * How the document actually reached the user.
   *
   * `'tab'` only when a PDF had a live tab waiting for it. A PDF whose tab was
   * refused falls back to `'download'` rather than failing: the user asked for
   * the file, and a saved PDF is a strictly better outcome than an error line
   * telling them to go change their pop-up settings.
   */
  deliveredVia: 'tab' | 'download'
}

/**
 * Saves the document to disk via a synthetic `<a download>`.
 *
 * Revokes immediately: the browser has committed to the bytes by the time
 * `click()` returns, so holding the blob any longer is pure memory pressure —
 * and it accumulates, since a user saving several statements in a row would
 * otherwise pin every one of them for a full minute.
 */
const saveToDisk = (
  objectUrl: string,
  fileName: string,
): StatementDocumentDeliveryResult => {
  const anchor = document.createElement('a')
  anchor.href = objectUrl
  anchor.download = fileName
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
  URL.revokeObjectURL(objectUrl)

  return { deliveredVia: 'download' }
}

/**
 * @param statementDocument - The fetched or generated document, already named.
 *   Ownership of its blob passes to this function, which revokes the url it
 *   creates.
 * @param pdfTab - For a PDF, a blank tab the CALLER opened synchronously inside
 *   the originating click, to be navigated here. `null` when the browser
 *   refused it, or for any non-PDF format. Opening it here instead would be
 *   refused outright by Safari and Firefox on the real download path, which
 *   reaches this function from a mutation callback, long after the gesture that
 *   started it — see `useStatementDownload`.
 */
export const deliverStatementDocument = (
  statementDocument: DeliverableStatementDocument,
  pdfTab: Window | null,
): StatementDocumentDeliveryResult => {
  const objectUrl = URL.createObjectURL(statementDocument.blob)

  if (statementDocument.format !== 'PDF' || !pdfTab) {
    return saveToDisk(objectUrl, statementDocument.fileName)
  }

  // Severed by ASSIGNMENT rather than the `noopener` window feature: that
  // feature makes `window.open` return `null` always — success and failure
  // alike (HTML standard, window open steps: "If noopener is true, then return
  // null") — which would destroy the caller's only signal that the tab exists.
  // The protection is the same either way: the document loses its handle back
  // into the authenticated app.
  pdfTab.opener = null
  pdfTab.location.href = objectUrl

  // Deferred, and only on this path: the tab has not finished navigating when
  // `location.href` returns, so revoking now would leave it blank. An
  // unrevoked object url pins its blob for the lifetime of the document.
  setTimeout(
    () => URL.revokeObjectURL(objectUrl),
    NEW_TAB_OBJECT_URL_LIFETIME_MS,
  )

  return { deliveredVia: 'tab' }
}
